import type { Step } from 'react-joyride';

/**
 * HOW TO ADD A PAGE TOUR
 * ----------------------
 * 1. Put data-tour="some-id" on the element you want to highlight.
 * 2. Add a Step below with target: '[data-tour="some-id"]'.
 * 3. Register those steps under the route path in tourStepsByPath.
 * 4. Open that page and click the "?" button in the navbar.
 */

/** Shared chrome available on most authenticated pages. */
export const shellTourSteps: Step[] = [
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Navigation',
    content: 'Use the sidebar to move between Dashboard, Records, and more.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-dashboard"]',
    title: 'Dashboard',
    content: 'Your overview: balances, KPI graphs, and record filter live here.',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-records"]',
    title: 'Records',
    content: 'Create and open financial record books. Each record holds its own transactions and accounts.',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-ai"]',
    title: 'AI Assistant',
    content: 'Open Balancio AI from the navbar anytime for help with your books.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="fab-ai"]',
    title: 'Floating AI button',
    content: 'The pulsing robot button also opens the AI chat from any page.',
    placement: 'left',
  },
];

/** /dashboard */
export const dashboardTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="dashboard-record-select"]',
    title: 'Choose a record',
    content: 'Filter the overview to one record, or leave it on All to see everything together.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-balances"]',
    title: 'Balances at a glance',
    content: 'Capital, Assets, and Liabilities update from your transactions for the selected record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-kpis"]',
    title: 'KPI graphs',
    content: 'Each card shows a live trend sparkline from your ledger history — not a static image.',
    placement: 'top',
  },
];

/** /records */
export const recordsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="records-new"]',
    title: 'Create a record',
    content: 'Start here to create a new financial record book (business, personal, or project).',
    placement: 'bottom',
  },
  {
    target: '[data-tour="records-search"]',
    title: 'Search records',
    content: 'Type a name to quickly find a record when you have many.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="records-list"]',
    title: 'View your records',
    content: 'Click a card to open that record’s transactions. Use the menu for edit, share, import/export, and permissions.',
    placement: 'top',
  },
];

/** /records/transactions */
export const transactionsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="nav-transactions"]',
    title: 'Transactions section',
    content: 'After you select a record, use this sidebar link anytime to return to its transactions.',
    placement: 'right',
  },
  {
    target: '[data-tour="transactions-new"]',
    title: 'Create a transaction',
    content: 'Add a new money movement for this record. You can describe it in plain language with AI, too.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="transactions-filters"]',
    title: 'Filters',
    content: 'Narrow by date, entity, amount, or who created the transaction.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="transactions-list"]',
    title: 'View transactions',
    content: 'Click a row to open details. Edit or delete from the row actions or the detail panel.',
    placement: 'top',
  },
];

/** /records/accounts */
export const accountsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="nav-accounts"]',
    title: 'Accounts section',
    content: 'Open ledgers, stock valuation, trading accounts, P&L, and balance sheets from here.',
    placement: 'right',
  },
  {
    target: '[data-tour="accounts-grid"]',
    title: 'Account books',
    content: 'Pick Ledgers for individual accounts, or open financial statements like Profit & Loss and Balance Sheets.',
    placement: 'top',
  },
  {
    target: '[data-tour="accounts-ledgers"]',
    title: 'Ledgers',
    content: 'View and manage all ledger accounts for the selected record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="accounts-pnl"]',
    title: 'Profit & Loss',
    content: 'Open your P&L statement for this record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="accounts-balance-sheet"]',
    title: 'Balance Sheets',
    content: 'View the balance sheet for assets, liabilities, and capital.',
    placement: 'bottom',
  },
];

/**
 * Map route pathnames → Joyride steps.
 */
const tourStepsByPath: Record<string, Step[]> = {
  '/dashboard': dashboardTourSteps,
  '/records': recordsTourSteps,
  '/records/transactions': transactionsTourSteps,
  '/records/accounts': accountsTourSteps,
};

/** Exact path match, then longest parent prefix, then shell fallback. */
export function getTourStepsForPath(pathname: string): Step[] {
  if (tourStepsByPath[pathname]) {
    return tourStepsByPath[pathname];
  }

  const parent = Object.keys(tourStepsByPath)
    .filter((path) => pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  if (parent) {
    return tourStepsByPath[parent];
  }

  return shellTourSteps;
}
